<script setup lang="ts">
import { Button, Slider } from 'primevue'
import { type ComputedRef, type Ref, computed, onMounted, ref, watch } from 'vue'
import { useProductStore } from '@/stores'
import { type DefaultMatrix, type MatrixInfo, type ProductStore } from '@/types'
import { formatMoney } from '@/utils'

defineEmits<{ (e: 'onButtonClick'): Awaited<Promise<void>> }>()

const productStore: ProductStore = useProductStore()

const offers: ComputedRef<DefaultMatrix> = computed((): DefaultMatrix => productStore.offers)

const maxCount: ComputedRef<number> = computed(
    (): number => offers.value[debtSlider.value - 1]?.length || 1,
)

const maxPayment: ComputedRef<number> = computed(
    (): number =>
        offers.value[debtSlider.value - 1]?.[minCount - 1]?.schedule[0].amountAfterDiscount || 0,
)

const minCount = 1

const minPayment: ComputedRef<number> = computed(
    (): number =>
        offers.value[debtSlider.value - 1]?.[maxCount.value - 1]?.schedule[0].amountAfterDiscount ||
        0,
)

const countSlider: Ref<number> = ref(minCount)
const debtSlider: Ref<number> = ref(1)
const paymentSlider: Ref<number> = ref(maxPayment.value)

const recalc = (type: 'payment' | 'count') => {
    if (type === 'payment') {
        let roundCount: number = Math.floor((debtSlider.value * 1000000) / paymentSlider.value)
        if (roundCount > maxCount.value) {
            roundCount = maxCount.value
        } else if (roundCount < minCount) {
            roundCount = minCount
        }
        countSlider.value = roundCount
    }
    paymentSlider.value =
        offers.value[debtSlider.value - 1]?.[countSlider.value - 1]?.schedule[0]
            .amountAfterDiscount || 0
}

const changeDebt = () => {
    if (!offers.value[debtSlider.value - 1][countSlider.value - 1]) {
        countSlider.value = minCount
    }
    recalc('count')
}

onMounted(() => {
    debtSlider.value =
        offers.value.findIndex(
            (offer: MatrixInfo[]) => offer[0].schedule[0].amountBeforeDiscount === 5000000,
        ) + 1
    recalc('count')
})

watch(
    () => offers.value,
    (data: Array<MatrixInfo[]>) => {
        if (data.length) {
            debtSlider.value =
                offers.value.findIndex(
                    (offer: MatrixInfo[]) => offer[0].schedule[0].amountBeforeDiscount === 5000000,
                ) + 1
            recalc('count')
        }
    },
)

// watch(
//     // TODO: wtf
//     () => maxCount.value,
//     (value: number) => {
//         if (selectedCount.value > value) {
//             selectedCount.value = value
//             countSlider.value = value
//             recalc('count')
//         }
//     },
// )
</script>

<template>
    <div
        class="z-10 min-w-[335px] shrink-0 rounded-3xl bg-surface-0 p-4 pb-4 text-primary-400 md:w-[475px] md:min-w-[475px] lg:p-6 lg:pb-4">
        <div class="text-mob-h4 text-primary-400 md:text-desk-h4">Рассчитайте свою выгоду</div>
        <div class="mt-4 flex flex-col gap-4">
            <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between text-primary-500">
                    <div class="text-body-16">Ваша задолженность</div>
                    <div class="text-desk-h4">
                        {{ formatMoney(debtSlider * 1000000, true) }}
                    </div>
                </div>
                <Slider
                    v-model="debtSlider"
                    :min="1"
                    :max="offers.length"
                    :step="1"
                    @change="changeDebt" />
                <div class="flex justify-between text-caption-xs-reg text-surface-500">
                    <div>{{ formatMoney(1000000, true) }}</div>
                    <div>{{ formatMoney(10000000, true) }}</div>
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between text-primary-500">
                    <div class="text-body-16">Размер платежа</div>
                    <div class="text-desk-h4">{{ formatMoney(paymentSlider, true) }}</div>
                </div>
                <Slider
                    v-model="paymentSlider"
                    :min="minPayment"
                    :max="maxPayment"
                    :step="10000"
                    @change="recalc('payment')" />
                <div class="flex justify-between text-caption-xs-reg text-surface-500">
                    <div>{{ formatMoney(minPayment, true) }}</div>
                    <div>{{ formatMoney(maxPayment, true) }}</div>
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between text-primary-500">
                    <div class="text-body-16">Количество платежей</div>
                    <div class="text-desk-h4">{{ countSlider }}</div>
                </div>
                <Slider
                    v-model="countSlider"
                    :min="minCount"
                    :max="maxCount"
                    @change="recalc('count')" />
                <div class="flex justify-between text-caption-xs-reg text-surface-500">
                    <div>{{ minCount }}</div>
                    <div>{{ maxCount }}</div>
                </div>
            </div>

            <div class="flex flex-col items-center gap-6 md:flex-row md:justify-between">
                <div
                    class="flex w-full items-center justify-between gap-0.5 rounded-lg bg-primary-100 p-3 md:w-auto md:flex-col md:items-start md:px-4 md:py-1.5">
                    <div class="text-desk-h5 text-surface-700">Простим</div>
                    <div class="flex items-center gap-2 md:flex-row-reverse md:gap-3">
                        <div
                            class="rounded bg-form-field-invalid-placeholder-color p-1.5 text-xl font-bold leading-5 text-surface-0">
                            {{ offers[debtSlider - 1]?.[countSlider - 1]?.percent || 0 }}%
                        </div>
                        <div class="text-mob-h4 text-primary-500 md:text-desk-h3">
                            {{
                                formatMoney(
                                    offers[debtSlider - 1]?.[countSlider - 1]
                                        ?.totalDiscountAmount || 0,
                                    true,
                                )
                            }}
                        </div>
                    </div>
                </div>
                <div
                    class="flex w-full items-center justify-between gap-4 md:w-auto md:flex-col md:items-start md:gap-0.5">
                    <div class="text-body-14 text-surface-700">Заплатите за все</div>
                    <div class="flex items-end gap-3 md:flex-row-reverse">
                        <div class="text-caption-xs-semib text-surface-700 line-through">
                            {{ formatMoney(debtSlider * 1000000, true) }}
                        </div>
                        <div class="text-mob-h4 text-primary-500 md:text-desk-h4">
                            {{
                                formatMoney(
                                    offers[debtSlider - 1]?.[countSlider - 1]?.totalPayableAmount ||
                                        0,
                                    true,
                                )
                            }}
                        </div>
                    </div>
                </div>
            </div>

            <Button
                label="Погасить с прощением"
                class="h-14 bg-base-green"
                rounded
                fluid
                @click="$emit('onButtonClick')" />
        </div>
    </div>
</template>
